/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import React, { Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Add from '@mui/icons-material/Add';
import ArrowRight from '@mui/icons-material/ArrowRight';
import DragIndicator from '@mui/icons-material/DragIndicator';
import MoreHoriz from '@mui/icons-material/MoreHoriz';

import { Button } from 'uikit/buttons';
import { Typo } from 'uikit/content';
import { Menu } from 'uikit/menus';
import { Tooltip } from 'uikit/tooltips';

import getMenuItems from '../../exceed/helpers/getMenuItems';

import styles from '../styles/defaultListItemComponents.module.scss';

// ACTIONS

const Actions = ({
  actionMenuItems = [],
  onIsOpenChange,
  isAddButtonDisabled = false,
  addButtonTooltipText = '',
}) => {
  if (actionMenuItems.length === 0) return null;

  const Element = isAddButtonDisabled ? Tooltip : Fragment;

  const tooltipProps = isAddButtonDisabled ? { content: addButtonTooltipText } : null;

  return (
    <Element {...tooltipProps}>
      <div className={styles.wrapper}>
        <Menu
          className={styles.element}
          horizontalPosition="left"
          shouldAnimate={false}
          onIsOpenChange={onIsOpenChange}
          component={
            <Button size="xsmall" variant="ghost" icon={<Add />} isDisabled={isAddButtonDisabled} />
          }
        >
          {getMenuItems(actionMenuItems)}
        </Menu>
      </div>
    </Element>
  );
};

// CONTENT

const Content = ({ item }) => {
  const { data, hasChildren } = item;
  const { title } = data;

  return (
    <div className={styles.content}>
      <Typo
        element={hasChildren ? 'h3' : 'h4'}
        variant={hasChildren ? 'b02' : 's01'}
        weight={hasChildren ? 'medium' : ''}
      >
        {title}
      </Typo>
    </div>
  );
};

Content.propTypes = {
  item: PropTypes.shape({
    data: PropTypes.shape({
      title: PropTypes.string,
    }),
    hasChildren: PropTypes.bool,
  }).isRequired,
};

// DRAG HANDLE

const DragHandle = ({
  item = {},
  provided = { dragHandleProps: {} },
  isDragDisabled = false,
  dragTooltipText = '',
}) => {
  const Element = isDragDisabled ? Tooltip : Fragment;
  const tooltipProps = isDragDisabled ? { content: dragTooltipText } : null;
  const dragHandleProps = { ...provided.dragHandleProps, tabIndex: 0, role: 'button' };

  return (
    <Element {...tooltipProps}>
      <div
        {...dragHandleProps}
        aria-disabled={isDragDisabled}
        data-testid={`draggable-list-item-drag-handle-${item.id}`}
      >
        <DragIndicator
          className={classNames(styles.dragHandle, {
            [styles.isDisabled]: isDragDisabled,
          })}
          sx={{ height: 24, width: 24 }}
        />
      </div>
    </Element>
  );
};

DragHandle.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  provided: PropTypes.shape({
    dragHandleProps: PropTypes.shape({}),
  }),
};

// KEBAB MENU

const KebabMenu = ({
  onIsOpenChange,
  kebabMenuItems = [],
  isKebabDisabled = false,
  kebabTooltipText = '',
}) => {
  if (kebabMenuItems.length === 0) return null;

  const Element = isKebabDisabled ? Tooltip : Fragment;
  const tooltipProps = isKebabDisabled ? { content: kebabTooltipText } : null;

  return (
    <Element {...tooltipProps}>
      <div>
        <Menu
          className={styles.kebabMenu}
          shouldAnimate={false}
          onIsOpenChange={onIsOpenChange}
          component={
            <Button
              size="xsmall"
              variant="ghost"
              icon={<MoreHoriz />}
              isDisabled={isKebabDisabled}
            />
          }
        >
          {getMenuItems(kebabMenuItems)}
        </Menu>
      </div>
    </Element>
  );
};

// TOGGLE EXPAND BUTTONs

const ToggleExpandButton = ({ item, onCollapse, onExpand }) => {
  const { isExpanded, children, hasChildren, id } = item;
  if (!hasChildren) return <div className={styles.toggleExpandButtonPlaceholder} />;

  const handleToggleExpandButton = () =>
    isExpanded ? onCollapse({ id, children }) : onExpand({ id, children });

  return (
    <Button
      onClick={handleToggleExpandButton}
      className={classNames(styles.toggleExpandButton, { [styles.isExpanded]: isExpanded })}
      variant="ghost"
      size="xsmall"
      isDisabled={!children.length}
      icon={<ArrowRight />}
    />
  );
};

ToggleExpandButton.propTypes = {
  item: PropTypes.shape({
    isExpanded: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.number),
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hasChildren: PropTypes.bool,
  }).isRequired,
  onCollapse: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired,
};

export default {
  Actions,
  Content,
  DragHandle,
  KebabMenu,
  ToggleExpandButton,
};
